import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowRight, Mail } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'

import { useRequestPasswordReset } from '../api/useForgotPassword'

const schema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

type Values = z.infer<typeof schema>

export function ForgotPasswordForm() {
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: '' } })
  const mutation = useRequestPasswordReset()

  const onSubmit = async (values: Values) => {
    try {
      const res = await mutation.mutateAsync({ email: values.email })
      const message = (res.data as any)?.message || 'If an account exists for that email, a reset link has been sent.'
      toast.success(message)
    }
    catch (err) {
      const message = (err as any)?.response?.data?.message || 'Unable to send reset email'
      toast.error(message)
    }
  }

  return (
    <Card className="w-full max-w-md mx-auto shadow-xl border-0 bg-card/50 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-2xl font-semibold">Forgot password</CardTitle>
        <CardDescription>Enter your email to receive a password reset link.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Email</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input type="email" className="pl-10 h-11" placeholder="you@example.com" {...field} />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button className="w-full h-11" disabled={mutation.isPending}>
              {mutation.isPending
                ? 'Sending…'
                : (
                  <span className="inline-flex items-center gap-2">
                    Send reset link
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
            </Button>
          </form>
        </Form>

        <div className="flex items-center gap-3">
          <Separator className="flex-1" />
          <span className="text-xs uppercase text-muted-foreground">OR</span>
          <Separator className="flex-1" />
        </div>

        <Button variant="link" className="w-full" asChild>
          <a href="/login">Back to login</a>
        </Button>
      </CardContent>
    </Card>
  )
}
